/**
 * Which property a record's title rename writes — or `null` when the title
 * can't be renamed in place.
 *
 * A record's title is its `ahoy_display_name`. For almost every type that is a
 * server-computed formula, so "is the display name a formula?" is not the useful
 * question; "does the display name resolve straight from ONE property the user
 * can write?" is. Renaming writes that property and the formula recomputes the
 * display name from it:
 *
 *   `ahoy_title or "Untitled Task"`       → `ahoy_title`   (task, meeting)
 *   `ahoy_name or ahoy_domain or "…"`     → `ahoy_name`    (company)
 *   no formula, `ahoy_name` present       → `ahoy_name`    (legacy types)
 *   no formula, no `ahoy_name`            → `ahoy_display_name` (direct name)
 *
 * and a display name that is COMPOSED or constant has no single source, so it
 * stays read-only — writing any one property would change nothing visible:
 *
 *   `(ahoy_first_name + " " + …) if … else …`   → null   (user, contact)
 *   `"Research"`                                → null
 *   `"Visit from " + ahoy_source + …`           → null
 *
 * Whether the resolved property is actually WRITABLE (schema write access,
 * field type, permission) is the caller's gate — this only answers "which
 * property". An empty schema map (still loading) resolves to null, so every
 * caller fails closed.
 */

import type { PropertyTypeSchema } from '@/lib/crm/hooks/usePropertySchema';

/**
 * A formula whose FIRST term is a bare property reference, followed either by
 * the end of the expression or by an `or` fallback chain. `first(ahoy_email)`
 * (a call), `"Research"` (a literal) and `(a + b)` (an expression) all fail it.
 */
const LEADING_PROPERTY_FORMULA = /^([a-z][a-z0-9_]*)(?:\s+or\s|\s*$)/;

const DISPLAY_NAME_PROPERTY = 'ahoy_display_name';
const LEGACY_NAME_PROPERTY = 'ahoy_name';

export function resolveTitlePropertyName(
  propertySchemas: Readonly<Record<string, PropertyTypeSchema | undefined>>
): string | null {
  const formula = propertySchemas[DISPLAY_NAME_PROPERTY]?.formula?.trim();
  if (!formula) {
    if (propertySchemas[LEGACY_NAME_PROPERTY]) {
      return LEGACY_NAME_PROPERTY;
    }
    // No legacy `ahoy_name`, but a direct (non-formula) `ahoy_display_name` is
    // itself the single source the title resolves from — renaming writes it
    // directly. Whether it's actually writable stays the caller's gate.
    return propertySchemas[DISPLAY_NAME_PROPERTY]
      ? DISPLAY_NAME_PROPERTY
      : null;
  }

  const candidate = LEADING_PROPERTY_FORMULA.exec(formula)?.[1];
  if (!candidate || candidate === DISPLAY_NAME_PROPERTY) {
    return null;
  }
  const candidateSchema = propertySchemas[candidate];
  // A derived source (itself a formula) can't be written either.
  if (!candidateSchema || candidateSchema.formula) {
    return null;
  }
  return candidate;
}
